import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';

import { adminItemKey } from '../_shared/queries';
import type { DocumentRow, Group, MediaAsset } from '../_shared/types';
import { putToPresignedTarget, uploadResumable } from '../_shared/upload';
import { documentsKey, encodeJobsKey, mediaAssetsKey, renditionsKey } from './queries';

/**
 * Every write the assets surface performs.
 *
 * Three rules bind all of them:
 *
 * - **`confirm=true` is the wire half of the dialog.** Every hard removal here —
 *   an episode, a season, the trailer, the film file — carries it, and it is set
 *   **only** from a `<ConfirmDialog>` accept handler. A code path that set it
 *   from anywhere else would have removed the guard while leaving the dialog on
 *   screen.
 * - **`catalogue_delete` is hard.** The record and its stored files go, and the
 *   confirmation is the whole safety mechanism — which is why it names what goes
 *   with the thing rather than asking a bare question. Deliberately unlike the
 *   editorial delete, which is a state.
 * - **Nothing auto-retries.** A removal whose outcome is unknown is not retried
 *   from the client: the copy says the outcome is unknown and directs to a
 *   reload rather than offering a button that might delete twice.
 */

function useAssetsInvalidation() {
  const queryClient = useQueryClient();

  return (contentItemId: string) => {
    void queryClient.invalidateQueries({ queryKey: mediaAssetsKey(contentItemId) });
    void queryClient.invalidateQueries({ queryKey: adminItemKey(contentItemId) });
  };
}

// ---- seasons ---------------------------------------------------------------

export function useCreateGroup() {
  const invalidate = useAssetsInvalidation();

  return useMutation({
    mutationFn: ({ contentItemId, title }: { contentItemId: string; title: string }) =>
      api.post<Group>('content', `/admin/contentItems/${contentItemId}/groups`, { title }),
    onSuccess: (_data, variables) => invalidate(variables.contentItemId),
    retry: false,
  });
}

export function useUpdateGroup() {
  const invalidate = useAssetsInvalidation();

  return useMutation({
    mutationFn: ({
      contentItemId,
      groupId,
      body,
    }: {
      contentItemId: string;
      groupId: string;
      body: { title?: string; description?: string | null };
    }) => api.patch<Group>('content', `/admin/contentItems/${contentItemId}/groups/${groupId}`, body),
    onSuccess: (_data, variables) => invalidate(variables.contentItemId),
    retry: false,
  });
}

/**
 * **Removing a season removes the episodes it holds.** A season is a partition
 * over the ordered assets, not a folder beside them — an operator who expected
 * the episodes to survive would be right to be surprised, and the surprise
 * belongs in the confirmation rather than after it.
 */
export function useDeleteGroup() {
  const invalidate = useAssetsInvalidation();

  return useMutation({
    mutationFn: ({ contentItemId, groupId }: { contentItemId: string; groupId: string }) =>
      api.delete<null>('content', `/admin/contentItems/${contentItemId}/groups/${groupId}?confirm=true`),
    onSuccess: (_data, variables) => invalidate(variables.contentItemId),
    retry: false,
  });
}

export function useReorderGroups() {
  const invalidate = useAssetsInvalidation();

  return useMutation({
    mutationFn: ({ contentItemId, orderedIds }: { contentItemId: string; orderedIds: string[] }) =>
      api.post<null>('content', `/admin/contentItems/${contentItemId}/groups:reorder`, {
        orderedIds,
      }),
    onSuccess: (_data, variables) => invalidate(variables.contentItemId),
    retry: false,
  });
}

// ---- assets ----------------------------------------------------------------

export function useUpdateMediaAsset() {
  const invalidate = useAssetsInvalidation();

  return useMutation({
    mutationFn: ({
      contentItemId,
      mediaAssetId,
      body,
    }: {
      contentItemId: string;
      mediaAssetId: string;
      body: { title?: string; description?: string | null };
    }) =>
      api.patch<MediaAsset>(
        'content',
        `/admin/contentItems/${contentItemId}/mediaAssets/${mediaAssetId}`,
        body,
      ),
    onSuccess: (_data, variables) => invalidate(variables.contentItemId),
    retry: false,
  });
}

/**
 * **Refused while the asset is the only one under a published title** — a
 * published page with nothing to play is not a valid state. The control still
 * ships and the dialog still opens; what changes is that it opens with no danger
 * button and the way out in its body.
 */
export function useDeleteMediaAsset() {
  const invalidate = useAssetsInvalidation();

  return useMutation({
    mutationFn: ({ contentItemId, mediaAssetId }: { contentItemId: string; mediaAssetId: string }) =>
      api.delete<null>(
        'content',
        `/admin/contentItems/${contentItemId}/mediaAssets/${mediaAssetId}?confirm=true`,
      ),
    onSuccess: (_data, variables) => invalidate(variables.contentItemId),
    retry: false,
  });
}

export function useReorderMediaAssets() {
  const invalidate = useAssetsInvalidation();

  return useMutation({
    mutationFn: ({ contentItemId, orderedIds }: { contentItemId: string; orderedIds: string[] }) =>
      api.post<null>('content', `/admin/contentItems/${contentItemId}/mediaAssets:reorder`, {
        orderedIds,
      }),
    onSuccess: (_data, variables) => invalidate(variables.contentItemId),
    retry: false,
  });
}

// ---- free preview ----------------------------------------------------------

/**
 * The free marking, which is **per asset on the wire even where the surface
 * offers a season-wide shortcut**.
 *
 * The entitlement lands on the asset, so a season-level flag would need
 * re-evaluating every time an episode moved between seasons. Pressing the season
 * writes every episode's flag, and the season chip reads its state back from
 * them — a season shown as free with one episode still behind the wall would be
 * a lie on the surface.
 *
 * A trailer is a free preview **by kind**, so the surface renders no control for
 * it and the service refuses a redundant write rather than accepting one.
 */
export function useSetFreePreview() {
  const invalidate = useAssetsInvalidation();

  return useMutation({
    mutationFn: ({ mediaAssetId, free }: { contentItemId: string; mediaAssetId: string; free: boolean }) =>
      api.post<{ isFreePreview: boolean }>(
        'assets',
        `/mediaAssets/${mediaAssetId}:${free ? 'markFreePreview' : 'clearFreePreview'}`,
      ),
    onSuccess: (_data, variables) => invalidate(variables.contentItemId),
    retry: false,
  });
}

// ---- uploads ---------------------------------------------------------------

interface UploadRegistration {
  id: string;
  uploadTarget: string;
}

/**
 * A media upload, in the three steps the contract has: the content service
 * creates the record, the assets service issues a presigned target, and
 * `:completeUpload` is the single commit point — it reads the object itself
 * rather than trusting a checksum this side declared.
 *
 * **Where the file lands is stated by where it was dropped.** The caller passes
 * the kind and the season; there is no second step that assigns it, because a
 * second step is a question whose answer can be given wrongly.
 */
export function useUploadMediaAsset() {
  const invalidate = useAssetsInvalidation();

  return useMutation({
    mutationFn: async ({
      contentItemId,
      kind,
      groupId,
      file,
      chunkSizeBytes,
      onProgress,
    }: {
      contentItemId: string;
      /** `TRAILER` · `FEATURE` · `EPISODE` — the drop *is* the assignment. */
      kind: string;
      groupId: string | null;
      file: File;
      /** Published by `GET /uploadPolicy`; never chosen on this side. */
      chunkSizeBytes: number;
      /** Fractional, `0..1`, drawn on the zone the file was dropped into. */
      onProgress: (fraction: number) => void;
    }) => {
      const record = await api.post<MediaAsset>(
        'content',
        `/admin/contentItems/${contentItemId}/mediaAssets`,
        { kind, groupId, title: file.name },
      );

      // Registration still comes first and still gates the declaration against the
      // configured formats and the configured cap. What changed is the transport
      // of the bytes alone: the presigned target it hands back goes unused for a
      // video, because a presigned PUT can neither be resumed nor report progress.
      await api.post<UploadRegistration>('assets', '/mediaAssets', {
        mediaAssetId: record.id,
        declaredContentType: file.type,
        declaredSizeBytes: file.size,
      });

      await uploadResumable(file, { mediaAssetId: record.id, chunkSizeBytes, onProgress });
      await api.post<null>('assets', `/mediaAssets/${record.id}:completeUpload`);

      return record;
    },
    onSuccess: (_data, variables) => invalidate(variables.contentItemId),
    retry: false,
  });
}

/**
 * A subtitle track, attached to whichever subject's block the operator opened.
 *
 * **Attaching is not synchronising.** The file carries its own timecodes and the
 * bytes are stored verbatim; no route here shifts, re-times, or converts one, and
 * none will be added without a catalog row asking for it. The hint beside the
 * control says so, because the alternative is an operator discovering a mistimed
 * track from a subscriber's report.
 */
export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      subjectType,
      subjectId,
      file,
    }: {
      subjectType: string;
      subjectId: string;
      file: File;
    }) => {
      const registration = await api.post<UploadRegistration>('assets', '/documents', {
        subjectType,
        subjectId,
        documentKind: 'SUBTITLE',
        label: file.name,
        declaredContentType: file.type,
        declaredSizeBytes: file.size,
      });

      await putToPresignedTarget(registration.uploadTarget, file);
      await api.post<null>('assets', `/documents/${registration.id}:completeUpload`);

      return registration;
    },
    onSuccess: (_data, variables) =>
      void queryClient.invalidateQueries({
        queryKey: documentsKey(variables.subjectType, variables.subjectId),
      }),
    retry: false,
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentId }: { documentId: string; subjectType: string; subjectId: string }) =>
      api.delete<null>('assets', `/documents/${documentId}`),
    onSuccess: (_data, variables) =>
      void queryClient.invalidateQueries({
        queryKey: documentsKey(variables.subjectType, variables.subjectId),
      }),
    retry: false,
  });
}

export type { DocumentRow };

// ---- encode jobs -----------------------------------------------------------

/**
 * Ask the encoder for another attempt over the same ladder.
 *
 * **This is the one retry on this surface, and it is not the auto-retry the note
 * above forbids.** A removal is retried blind because its outcome is unknown; an
 * encode job states its own outcome, and re-running one that failed is
 * idempotent by construction — the service produces the rungs that are still
 * missing and leaves the ones that landed. It fires only from the operator's own
 * press on a job that reports `FAILED`.
 */
export function useRetryEncodeJob(mediaAssetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (encodeJobId: string) => api.post<unknown>('assets', `/encodeJobs/${encodeJobId}:retry`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: encodeJobsKey(mediaAssetId) });
      void queryClient.invalidateQueries({ queryKey: renditionsKey(mediaAssetId) });
    },
  });
}
